import * as React from 'react'
import { useRef, useState } from 'react'
import { cn } from '#app/shared/lib/misc.js'
import { inputCommonClassName } from './Input.tsx'

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  variant?: 'filled' | 'outlined' | 'outlined-label'
  textareaClassName?: string
  leftIcon?: React.ReactNode
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      id,
      value,
      placeholder,
      leftIcon,
      variant = 'filled',
      textareaClassName,
      ...props
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLTextAreaElement | null>(null)
    const [isFocused, setIsFocused] = useState(false)

    const labelShowPlaceholderStyles = `
        peer-placeholder-shown:top-2.5 peer-placeholder-shown:translate-y-0  peer-placeholder-shown:scale-100
        peer-placeholder-shown:text-foreground-muted peer-placeholder-shown:font-normal     `
    const labelShowLabelStyles = `
        left-5 top-0 -translate-y-2 scale-75 duration-200 origin-top-left text-foreground-muted font-semibold peer-focus:font-semibold
        peer-focus:left-5 peer-focus:top-0 peer-focus:-translate-y-2 peer-focus:scale-75 peer-focus:text-foreground
`

    const handleFocus = (event: any) => {
      props.onFocus && props.onFocus(event)
      setIsFocused(true)
    }
    const handleBlur = (event: any) => {
      props.onBlur && props.onBlur(event)
      setIsFocused(false)
    }
    const Comp = variant === 'outlined-label' ? 'fieldset' : 'div'
    return (
      <Comp
        className={cn(
          `
            relative min-h-[80px] w-full cursor-text rounded-lg border
            border-input px-4

            focus:border-2

            hover:border-foreground

            ${isFocused && `border-2 border-foreground`}
          `,
          props.readOnly && '!cursor-auto !border !border-input',
          className,
        )}
      >
        <textarea
          ref={(el) => {
            // Assign the input ref to the provided ref
            if (ref) {
              if (typeof ref === 'function') {
                ref(el)
              } else if (ref.hasOwnProperty('current')) {
                // @ts-ignore
                ref.current = el
              }
            }
            inputRef.current = el
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          id={id ?? placeholder}
          aria-describedby="filled_success_help"
          className={cn(
            inputCommonClassName,
            `
              peer -ml-4 block h-full min-h-[80px] w-[calc(100%+32px)]
              appearance-none !rounded-lg border-none bg-white py-3 pl-5 text-sm
              text-foreground

              focus:ring-0
            `,
            leftIcon && 'pl-10',
            textareaClassName,
          )}
          placeholder={(variant !== 'outlined-label' && placeholder) || ' '}
          name={`input-${placeholder}`}
          value={value}
          {...props}
        />
        {variant === 'outlined-label' && (
          <legend
            className={`
              invisible h-[5px] px-1 text-xs

              peer-focus:block

              peer-placeholder-shown:hidden
            `}
          >
            {placeholder}
          </legend>
        )}
        {leftIcon && (
          <div
            role="button"
            tabIndex={0}
            className={`
              absolute left-4 top-0 flex h-full cursor-text items-center
              text-foreground-muted
            `}
            onClick={() => inputRef.current!.focus()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                inputRef.current!.focus()
              }
            }}
          >
            {leftIcon}
          </div>
        )}
        {variant === 'outlined-label' && (
          <label
            htmlFor={id ?? placeholder}
            className={cn(
              'absolute cursor-text font-normal',
              labelShowLabelStyles,
              labelShowPlaceholderStyles,
              leftIcon &&
                `
                  peer-focus:pl-0

                  peer-placeholder-shown:pl-5
                `,
              props.readOnly && '!text-foreground-muted',
            )}
          >
            {placeholder}
          </label>
        )}
      </Comp>
    )
  },
)
Textarea.displayName = 'Textarea'

export { Textarea }
